/**
 * 報價單 Excel 匯出（需求 R14）
 * 以 ExcelJS 4.4 重建版面，欄位／配色／區塊位置與 quotePdfExport.js / a2-報價單實際畫面-示意圖.html 對齊。
 *
 * 字級換算說明（重要）：
 *   PDF（HTML/CSS）以 px 為單位、Excel font.size 以 pt 為單位，1pt ≈ 1.333px。
 *   舊版誤把 PDF 的 px 數值直接當成 Excel pt 使用，導致 Excel 內所有字級放大約 33%
 *   （此即 PM 在 PDF / Excel 截圖比對時看到「字級偏大」的根因）。
 *   本版改採 PDF px × 0.75 → Excel pt 的標準換算，視覺大小與 PDF 一致。
 *   font-weight 500 / 700 一律對映為 Excel bold。
 *
 *
 * 字級對照表（客戶資料 / 產品資訊 — 回應 需求 R14 PM 留言）
 *
 *
 * ▸ 客戶資料區塊（PDF .info-section / Excel A6:F9）
 *   ──────────────────────────────┬──── PDF ────┬──── Excel ────┬── SZ key
 *   區塊標題「客戶資料」h3        │ 14px bold   │ 10.5pt bold   │ SZ.h3
 *   標籤（客戶名稱/聯絡電話/信箱）│ 14px 500    │ 10.5pt bold   │ SZ.body
 *   值（公司名/電話/email）       │ 14px 500    │ 10.5pt bold   │ SZ.body
 *
 *   ※ 同一區塊「單據資料」採同樣字級，不另列。
 *
 * ▸ 產品資訊欄位（PDF td.product-cell / Excel B12+）
 *   ──────────────────────────────┬──── PDF ────┬──── Excel ────┬── SZ key
 *   表頭中文「產品資訊」          │ 13px 500    │ 10pt bold     │ SZ.th
 *   表頭英文「Product Information」│ 10px 400   │ 7.5pt         │ SZ.thEn
 *   產品中文品名 .product-name    │ 13px 500    │ 10pt bold     │ SZ.prodZh
 *   產品英文品名 .product-name-en │ 11px        │ 8pt           │ SZ.prodEn
 *   產品編號 .product-code        │ 11px        │ 8pt           │ SZ.code
 *   產品圖 placeholder「產品圖」  │ 10px        │ 7.5pt         │ SZ.caption
 *
 */

// 需求 R96：備註欄（葷素備註在上、業務備註在下）的組行規則與 PDF 匯出共用同一份實作。
import { buildRemarkText } from './quoteRemark';
// 需求 R111：金額欄（單價／箱價）的幣別位數與無條件進位規則同樣與 PDF 匯出共用一份，
// 理由同上——兩條路徑各寫一次就會漂移成「PDF 對、Excel 錯」（需求 R76 的教訓）。
import { fmtMoney, calcBoxPrice } from './quoteMoney';
// 需求 R165 Q1=A（PM 2026-09-07 改判）：箱價回到「印出來的單價 × 箱入數」，不再需要
// quoteUnitPrice 的未進位基準（resolveBoxUnitPriceBase），故本檔已移除該 import。
// 需求 R121 R2：保存期限的取值（快照 → populate 產品 → 實時產品）三層 fallback 與 PDF 共用。
import { getItemShelfLife } from './quoteShelfLife';

export const C = {
  badge:       'FFFFD54F', // 金色
  dark:        'FF5D4037', // 深棕
  mid:         'FF8D6E63', // 中棕
  light:       'FFFFFBF0', // 淡米黃
  headerBg:    'FFFFECB3', // 表頭米黃
  border:      'FFE6C9A3', // 棕邊
  // 需求 R91：原 productCode: 'FFF0E0B2' 在本檔的唯一用途是「產品圖」placeholder 底色，
  // 該處已改為 solid(C.white)，此常數隨之無任何引用故移除；報價單 Excel 匯出已無 #F0E0B2。
  // （PDF 端 quotePdfExport.js `.product-code` 產品編號膠囊的 #F0E0B2 屬編號膠囊、依 AC6 刻意保留。）
  white:       'FFFFFFFF',
  red:         'FFEF4444', // estimate 警告
  codeText:    'FF6D4C41', // 產品編號字色
};

// PDF px → Excel pt 對照（× 0.75）：
// 20px → 15pt   (公司中文名 h1)
// 14px → 10.5pt (英文名 / h2 標題 / contact-name / h3 / info-row / TRADE TERMS)
// 13px → 10pt   (表頭中文 / 產品名中文 / 體積 / 備註)
// 12px → 9pt    (地址 / Tel-Fax / contact-label / contact-info / 規格 / 單價)
// 11px → 8pt    (產品名英文 / 產品編號)
// 10px → 7.5pt  (表頭英文 / 產品圖 placeholder)
export const SZ = {
  h1: 15, h2: 10.5, h3: 10.5,
  body: 10.5, // info-label/value、TRADE TERMS 14px
  th: 10, thEn: 7.5,
  td: 10,    // 預設 td 13px（體積、備註）
  spec: 9,   // 規格 12px
  price: 9,  // 單價 12px
  prodZh: 10, // 產品名中文 13px
  prodEn: 8,  // 產品名英文 11px
  code: 8,    // 產品編號 11px
  small: 9,   // 地址 / Tel-Fax / contact-label / contact-info 12px
  caption: 7.5, // 產品圖 placeholder 10px
};

export const CURRENCY_LABEL = { TWD: 'NT$', HKD: 'HK$', USD: 'US$', EUR: '€' };
// PDF 使用 'Noto Sans TC'；Excel 端以 Microsoft JhengHei 作為 Windows 後援字型（與 需求 R14 規格書一致）。
export const FONT = { name: 'Microsoft JhengHei' };

// 非金額數字（材積 cuft 等）用的通用格式化，預設一位小數。
// 需求 R111：金額欄已改用 quoteMoney 的 fmtMoney(value, currency)，不再走這支——這裡的
// d = 1 預設值正是 PM 回報「美金匯出只顯示到小數第 1 位」的根因（PDF 端有一份一模一樣的）。
// 需求 R139（PM 2026-08-25 拍板 Q1=A）：材積 cuft 改在呼叫點帶 fmt(cuft, 2)，
// 位數不足補零（1.2 → 1.20），與畫面及 PDF 端逐格一致。預設值維持 1 不動。
export function fmt(v, d = 1) {
  if (v == null) return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toLocaleString('en-US', {
    minimumFractionDigits: d, maximumFractionDigits: d,
  });
}

export function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

export async function fetchCompanyInfo(companyLabel) {
  try {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch('/api/companies', { headers });
    if (!res.ok) throw new Error('Failed to fetch companies');
    const companies = await res.json();
    const match = companies.find((c) => c.label === companyLabel) || companies[0];
    if (!match) return {};
    return {
      name: match.name || match.label || '',
      nameEn: match.nameEn || '',
      logoUrl: match.logoUrl || '',
      address: match.address || '',
      addressEn: match.addressEn || '',
      tel: match.tel || '',
      fax: match.fax || '',
    };
  } catch (err) {
    console.error('[quoteExcelExport] fetchCompanyInfo failed:', err);
    return {};
  }
}

export async function fetchImageData(url) {
  if (!url) return null;
  try {
    const fullUrl = url.startsWith('http')
      ? url
      : `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
    const res = await fetch(fullUrl);
    if (!res.ok) return null;
    const blob = await res.blob();
    const buffer = await blob.arrayBuffer();
    let extension = 'png';
    if (blob.type.includes('jpeg') || blob.type.includes('jpg')) extension = 'jpeg';
    else if (blob.type.includes('gif')) extension = 'gif';

    // 取得原始尺寸供 fit-contain 縮放使用，避免 ExcelJS ext 強制拉伸成方形造成圖片變形（PM 留言 2026-05-18）。
    const { naturalWidth, naturalHeight } = await new Promise((resolve) => {
      const objectUrl = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        URL.revokeObjectURL(objectUrl);
        resolve({ naturalWidth: w, naturalHeight: h });
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({ naturalWidth: 0, naturalHeight: 0 });
      };
      img.src = objectUrl;
    });

    return { buffer, extension, naturalWidth, naturalHeight };
  } catch (err) {
    console.warn('[quoteExcelExport] fetchImageData failed:', url, err);
    return null;
  }
}

// fit-contain：在 boxW × boxH 內保持原比例縮放，避免圖片變形。
export function fitContain(naturalW, naturalH, boxW, boxH) {
  if (!naturalW || !naturalH) return { width: boxW, height: boxH };
  const ratio = Math.min(boxW / naturalW, boxH / naturalH);
  return { width: naturalW * ratio, height: naturalH * ratio };
}

// PDF spec：1px @ 96 DPI = 9525 EMU。Excel xdr:colOff/xdr:rowOff 一律以 EMU 為單位。
const EMU_PER_PX = 9525;

// 欄寬（字元）→ 實際像素：本檔唯一的換算來源（需求 R134 AC7）
// Excel 的 <col width="n"> 換算成螢幕像素並非 n × MDW，而是 n × MDW + padding：
// px = 字元數 × MDW(7, Calibri 11 的最大數字寬) + 5（儲存格左右內距合計）
// 驗證：預設欄寬 8.43 → 8.43×7+5 ≈ 64px、欄寬 10 → 75px，皆與 Excel 實際顯示相符。
//
// 需求 R134（PM 2026-08-21 Q4=A「就是要修掉約 2.5px 的左偏，數學上真正置中」）：
// 舊版 Logo 與品項縮圖各自寫死 `const cellW = 12 * 7`（= 84px），少算了那 5px padding，
// 置中時 (cellW - imgW)/2 因此比實際少 2.5px ⇒ 圖片一律偏左 2.5px。
// Q3=A「一併修（同一個換算 bug，改一次兩處都正）」→ 兩處統一改用本函式，
// pxToNativeCol 走訪欄位時也用同一份，未來改欄寬不會再有第二份定義漂移。
const EXCEL_MDW_PX = 7;      // 最大數字字元寬
const EXCEL_COL_PADDING_PX = 5; // 儲存格左右內距 + 格線
export const colWidthToPx = (widthCh) => widthCh * EXCEL_MDW_PX + EXCEL_COL_PADDING_PX;

// 6 欄的欄寬（字元）— sheet.columns 與圖片置中換算共用這一份，不得各自寫死。
//
// 需求 R135（PM 2026-08-24 附圖「原始欄寬 / 修改欄寬」）：
// ExcelJS 寫入的 column.width 與 Excel「欄寬」對話框顯示值差一個儲存格內距：
// Excel 顯示值 = 程式值 − 0.625
// 以改版前的程式值 [12,20,17,16,14,14] 換算，Excel 顯示恰為
// 11.38 / 19.38 / 16.38 / 15.38 / 13.38 / 13.38，與 PM 附圖「原始欄寬」六欄完全吻合，
// 可確認附圖量測對象即本檔。PM 的目標顯示值 13.38 / 25.38 / 22.38 / 15.38 / 22.38 / 22.38
// 反推程式值即下方這一行（D 欄單價維持 16 不動）。
//
// A=Logo/縮圖 (14 ≈ 103px，可容放大後的 92x92 logo) | B=產品文字 (26 ≈ 187px)
// C=規格 (23 ≈ 166px) | D=單價 (16 ≈ 117px，不動) | E=材積條碼 (23 ≈ 166px) | F=備註 (23 ≈ 166px)
// 六欄合計 681px → 905px（+33%）；pageSetup 為 fitToPage/fitToWidth:1，列印仍是 A4 直式單頁寬，
// 只是整體縮放比例再縮小約 25%（PM Q4「可」接受）。
const COL_WIDTHS_CH = [14, 26, 23, 16, 23, 23];

// 圖片尺寸上限（px）— fit-contain 的外框，兩者都只在本檔各自唯一一處被引用。
// 需求 R135 Q3（PM 2026-08-24 回「放大」）：A 欄由 89px 加寬到 103px 後，圖片不再維持原尺寸
// 只增加左右留白，而是跟著放大：
// · 品項縮圖 55 → 70（PM 於 Q3 直接給的數字）。A 欄 103px，70x70 水平仍留白 ±16.5px。
// 列高於 2026-08-27 再由 72pt 調到 108pt ≈ 144px，垂直留白由 ±13px 增為 ±37px；
// PM 本次只指示列高，未指示放大圖片，故 ITEM_THUMB_PX 維持 70 不動。
// · Logo 80 → 92（PM 只點名縮圖，Logo 依同一比例放大：原 80/89 ≈ 89.9% 欄寬 → 92/103 ≈ 89.3%）。
// A1:A4 合併列高 30+22+20+20 = 92pt ≈ 122.67px，92px 高仍有 ±15px 餘裕。
// 兩者皆走 fitContain()，非正方形圖片只會等比放大、不會變形。
export const LOGO_BOX_PX = 92;
export const ITEM_THUMB_PX = 70;

// 重要：ExcelJS 的 set col(v) / set row(v)（lib/doc/anchor.js）將 fractional 座標
// 換算為 EMU 時，採用 colWidth = column.width × 10000（而非實際 EMU 寬度），
// 導致 fractional col=0.173 寫出的 colOff 比實際視覺位置縮水約 6.7×，
// 這就是 PM 2026-05-18 留言「圖片沒有置中」的真實原因。
//
// 解法：直接以 nativeCol / nativeColOff / nativeRow / nativeRowOff 提供 anchor
// （anchor.js 第 20–24 行會直接保留 native 值），不經過 buggy 的 col/row setter，
// EMU 值由我們以「px × 9525」自行算出，確保 Excel 渲染與設計值一致。
//
// colWidthsCh 單位為 Excel 欄寬字元（見 colWidthToPx）。
// rowHeightsPt 單位為 pt（1pt = 4/3 px）。
export function pxToNativeCol(colStart0, colWidthsCh, offsetPx) {
  let remaining = Math.max(0, offsetPx);
  for (let i = 0; i < colWidthsCh.length; i++) {
    const px = colWidthToPx(colWidthsCh[i]);
    if (remaining < px) {
      return { nativeCol: colStart0 + i, nativeColOff: Math.floor(remaining * EMU_PER_PX) };
    }
    remaining -= px;
  }
  return { nativeCol: colStart0 + colWidthsCh.length, nativeColOff: 0 };
}

export function pxToNativeRow(rowStart0, rowHeightsPt, offsetPx) {
  let remaining = Math.max(0, offsetPx);
  for (let i = 0; i < rowHeightsPt.length; i++) {
    const px = rowHeightsPt[i] * 4 / 3;
    if (remaining < px) {
      return { nativeRow: rowStart0 + i, nativeRowOff: Math.floor(remaining * EMU_PER_PX) };
    }
    remaining -= px;
  }
  return { nativeRow: rowStart0 + rowHeightsPt.length, nativeRowOff: 0 };
}

export async function exportQuoteExcel({ quote }) {
  const { Workbook } = await import('exceljs');

  const co = await fetchCompanyInfo(quote.companyHeader);
  const items = quote.items || [];
  const contact = quote.contactPerson || {};
  const currency = quote.currency || 'TWD';
  const currLabel = CURRENCY_LABEL[currency] || currency;
  const custContact = (quote.customer?.contacts || [])[0] || {};
  const custPhone = custContact.phone || '';
  const custEmail = custContact.email || '';

  // 預先抓取所有圖片，避免 await 與 ExcelJS 寫入交錯造成圖片遺失
  const logoData = co.logoUrl ? await fetchImageData(co.logoUrl) : null;
  const productImgMap = {};
  await Promise.all(items.map(async (item, idx) => {
    const img = item.product?.productImage;
    if (img) {
      const data = await fetchImageData(img);
      if (data) productImgMap[idx] = data;
    }
  }));

  const workbook = new Workbook();
  const sheet = workbook.addWorksheet('報價單', {
    pageSetup: {
      paperSize: 9, // A4
      orientation: 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      horizontalCentered: true,
      margins: { left: 0.3, right: 0.3, top: 0.3, bottom: 0.3, header: 0, footer: 0 },
    },
  });

  // 欄寬定義見檔頭 COL_WIDTHS_CH（需求 R134 AC7：與圖片置中換算共用同一份；需求 R135 只改那一份常數）
  sheet.columns = COL_WIDTHS_CH.map((width) => ({ width }));

  const solid = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
  const bs = (argb, style = 'thin') => ({ style, color: { argb } });
  const cl = (r, c) => sheet.getCell(r, c);

  const fillRect = (r1, c1, r2, c2, argb) => {
    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        cl(r, c).fill = solid(argb);
      }
    }
  };

  // HEADER（公司資訊 + 標題色塊 + 聯絡人）
  fillRect(1, 1, 4, 6, C.light);
  // 4 列共 92pt（≈ 123px）才能容下 80x80 px logo（≈ 60pt）並保留呼吸空間。
  // 字級已 ×0.75 縮小，列高保持原值僅是讓視覺更清爽，不會出現擠壓。
  sheet.getRow(1).height = 30;
  sheet.getRow(2).height = 22;
  sheet.getRow(3).height = 20;
  sheet.getRow(4).height = 20;

  // Logo（A1:A4，80x80 ≈ 列高總和對應）
  sheet.mergeCells(1, 1, 4, 1);
  const logoCell = cl(1, 1);
  logoCell.fill = solid(C.light);
  logoCell.alignment = { vertical: 'middle', horizontal: 'center' };
  if (logoData) {
    const logoId = workbook.addImage({ buffer: logoData.buffer, extension: logoData.extension });
    // A1:A4 合併：欄寬 14ch = 14×7+5 = 103px、列高合計 30+22+20+20=92pt ≈ 122.67px。
    // fit-contain 至 LOGO_BOX_PX 見方後置中放置，保持原始比例不變形（非正方形 Logo 亦然）。
    // 需求 R134：cellW 改用 colWidthToPx()——舊版 12×7=84px 少算 5px padding，
    // 造成 (cellW-lw)/2 少 2.5px、Logo 一律左偏（PM 2026-08-21 附圖紫框處）。
    // 需求 R135：欄寬 12→14ch 後，本處因為讀 COL_WIDTHS_CH[0] 自動跟著重算（無第二份寫死值）；
    // 圖片上限 80→LOGO_BOX_PX(92) 為 Q3「放大」。Header 四列列高本次不動（Q2）。
    const cellW = colWidthToPx(COL_WIDTHS_CH[0]);
    const logoRows = [30, 22, 20, 20];
    const cellH = logoRows.reduce((s, h) => s + h, 0) * 4 / 3;
    const { width: lw, height: lh } = fitContain(logoData.naturalWidth, logoData.naturalHeight, LOGO_BOX_PX, LOGO_BOX_PX);
    const tlCol = pxToNativeCol(0, [COL_WIDTHS_CH[0]], (cellW - lw) / 2);
    const tlRow = pxToNativeRow(0, logoRows, (cellH - lh) / 2);
    sheet.addImage(logoId, {
      tl: { ...tlCol, ...tlRow },
      ext: { width: lw, height: lh },
      editAs: 'oneCell',
    });
  }

  // 公司中文名（B1:D1）— PDF h1 20px dark bold → 15pt
  sheet.mergeCells(1, 2, 1, 4);
  const cn = cl(1, 2);
  cn.value = co.name || '';
  cn.fill = solid(C.light);
  cn.font = { ...FONT, size: SZ.h1, bold: true, color: { argb: C.dark } };
  cn.alignment = { vertical: 'middle', horizontal: 'left' };

  // 公司英文名（B2:D2）— PDF .company-name-en 14px mid → 10.5pt
  sheet.mergeCells(2, 2, 2, 4);
  const cne = cl(2, 2);
  cne.value = co.nameEn || '';
  cne.fill = solid(C.light);
  cne.font = { ...FONT, size: SZ.body, color: { argb: C.mid } };
  cne.alignment = { vertical: 'middle', horizontal: 'left' };

  // 地址（B3:D3）— PDF .company-info p 12px mid → 9pt
  sheet.mergeCells(3, 2, 3, 4);
  const ca = cl(3, 2);
  ca.value = [co.address, co.addressEn].filter(Boolean).join(' / ');
  ca.fill = solid(C.light);
  ca.font = { ...FONT, size: SZ.small, color: { argb: C.mid } };
  ca.alignment = { vertical: 'middle', horizontal: 'left' };

  // Tel/Fax（B4:D4）— PDF p 12px mid → 9pt
  sheet.mergeCells(4, 2, 4, 4);
  const ct = cl(4, 2);
  ct.value = [
    co.tel ? `Tel: ${co.tel}` : '',
    co.fax ? `Fax: ${co.fax}` : '',
  ].filter(Boolean).join('   ');
  ct.fill = solid(C.light);
  ct.font = { ...FONT, size: SZ.small, color: { argb: C.mid } };
  ct.alignment = { vertical: 'middle', horizontal: 'left' };

  // 標題色塊（E1:F1）— PDF h2 14px dark bold + #FFD54F 背景 → 10.5pt
  sheet.mergeCells(1, 5, 1, 6);
  const tt = cl(1, 5);
  tt.value = '報價單｜QUOTATION';
  tt.fill = solid(C.badge);
  tt.font = { ...FONT, size: SZ.h2, bold: true, color: { argb: C.dark } };
  tt.alignment = { vertical: 'middle', horizontal: 'center' };

  // 聯絡人姓名（E2:F2）— PDF .contact-label 12px mid (9pt) + .contact-name 14px dark 500 (10.5pt bold)
  sheet.mergeCells(2, 5, 2, 6);
  const ctn = cl(2, 5);
  ctn.fill = solid(C.light);
  ctn.alignment = { vertical: 'middle', horizontal: 'right', wrapText: true };
  if (contact.name) {
    // 需求 R81 D2（PM 2026-07-22 拍板）：「聯絡人 Contact」區塊標題保留；僅移除欄位級標題
    ctn.value = {
      richText: [
        { text: '聯絡人 Contact ', font: { ...FONT, size: SZ.small, color: { argb: C.mid } } },
        { text: contact.name, font: { ...FONT, size: SZ.body, bold: true, color: { argb: C.dark } } },
      ],
    };
  }

  // E-mail（E3:F3）— PDF .contact-info 12px mid → 9pt
  // 需求 R81：聯絡人資料改顯示 E-mail（無「標題」，直接呈現值）
  sheet.mergeCells(3, 5, 3, 6);
  const ctp = cl(3, 5);
  ctp.fill = solid(C.light);
  ctp.alignment = { vertical: 'middle', horizontal: 'right' };
  if (contact.email) {
    ctp.value = contact.email;
    ctp.font = { ...FONT, size: SZ.small, color: { argb: C.mid } };
  }

  // E4:F4 保留版位（需求 R81：通訊方式/原 LINE ID 不顯示於匯出）
  sheet.mergeCells(4, 5, 4, 6);
  const ctl = cl(4, 5);
  ctl.fill = solid(C.light);
  ctl.alignment = { vertical: 'middle', horizontal: 'right' };

  // 列 5：金色分隔條（對應 PDF 表頭底部 2px 金線）
  sheet.getRow(5).height = 4;
  fillRect(5, 1, 5, 6, C.badge);

  // 雙欄資訊區（單據資料 / 客戶資料）
  const infoBorder = {
    top: bs(C.border), left: bs(C.border),
    right: bs(C.border), bottom: bs(C.border),
  };

  sheet.getRow(6).height = 22;
  // 左：單據資料 標題（A6:C6）— PDF h3 14px mid bold → 10.5pt
  sheet.mergeCells(6, 1, 6, 3);
  const lh = cl(6, 1);
  lh.value = '單據資料';
  lh.fill = solid(C.light);
  lh.font = { ...FONT, size: SZ.h3, bold: true, color: { argb: C.mid } };
  lh.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  lh.border = infoBorder;

  // 右：客戶資料 標題（D6:F6）— PDF h3 14px mid bold → 10.5pt
  sheet.mergeCells(6, 4, 6, 6);
  const rh = cl(6, 4);
  rh.value = '客戶資料';
  rh.fill = solid(C.light);
  rh.font = { ...FONT, size: SZ.h3, bold: true, color: { argb: C.mid } };
  rh.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  rh.border = infoBorder;

  // PDF info-label / info-value 都是 14px font-weight 500 → 全部 bold 10.5pt；label 用 mid、value 用 dark
  //
  // 需求 R134（PM 2026-08-21 拍板）：
  // 單據資料（B:C 合併）與客戶資料（E:F 合併）的**值**由 horizontal:'right' 改為 'left'。
  // Q2=A「沿用現有 indent:1，值與標籤同縮排，視覺成一直線」→ indent 維持 1，不改 0。
  // Q1=B「Excel 與 PDF 一起改」→ quotePdfExport.js 的 .info-row 同步改為值靠左。
  // 垂直維持 middle；字體／字級／粗體／顏色／底色／框線一律不動。
  // A 欄／D 欄標籤本來就是 left+indent:1，本次不動（AC3）。
  const writeInfoRow = (r, leftLabel, leftVal, rightLabel, rightVal) => {
    sheet.getRow(r).height = 20;
    // 左：標籤（A）+ 值（B:C）
    const ll = cl(r, 1);
    ll.value = leftLabel;
    ll.fill = solid(C.light);
    ll.font = { ...FONT, size: SZ.body, bold: true, color: { argb: C.mid } };
    ll.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    ll.border = infoBorder;
    sheet.mergeCells(r, 2, r, 3);
    const lv = cl(r, 2);
    lv.value = leftVal;
    lv.fill = solid(C.light);
    lv.font = { ...FONT, size: SZ.body, bold: true, color: { argb: C.dark } };
    lv.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }; // 需求 R134：right → left
    lv.border = infoBorder;
    // 右：標籤（D）+ 值（E:F）
    const rl = cl(r, 4);
    rl.value = rightLabel;
    rl.fill = solid(C.light);
    rl.font = { ...FONT, size: SZ.body, bold: true, color: { argb: C.mid } };
    rl.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    rl.border = infoBorder;
    sheet.mergeCells(r, 5, r, 6);
    const rv = cl(r, 5);
    rv.value = rightVal;
    rv.fill = solid(C.light);
    rv.font = { ...FONT, size: SZ.body, bold: true, color: { argb: C.dark } };
    rv.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }; // 需求 R134：right → left
    rv.border = infoBorder;
  };

  writeInfoRow(7, '報價單編號', quote.quoteNumber || '—', '客戶名稱', quote.customerName || '—');
  writeInfoRow(8, '報價日期', fmtDate(quote.quoteDate), '聯絡電話', custPhone || '—');
  writeInfoRow(9, '有效期限', fmtDate(quote.expiryDate), '電子信箱', custEmail || '—');

  // 品項表
  sheet.getRow(10).height = 6; // spacer

  const tableHeaderRow = 11;
  sheet.getRow(tableHeaderRow).height = 36;

  const headerBorder = {
    top: bs(C.border),
    left: bs(C.border),
    right: bs(C.border),
    bottom: bs(C.badge, 'medium'),
  };

  // PDF th 中文 13px dark 500 (10pt bold) / .th-en 10px mid 400 (7.5pt) → richText 兩段
  // PDF .items-table th 預設 text-align:left；只有 .text-center 欄位置中。
  // 對應到 Excel：第 1 欄（產品資訊）左對齊、其餘 4 欄置中。
  const writeHeader = (col1, col2, zh, en, align = 'center') => {
    if (col1 !== col2) sheet.mergeCells(tableHeaderRow, col1, tableHeaderRow, col2);
    for (let c = col1; c <= col2; c++) {
      const cell = cl(tableHeaderRow, c);
      cell.fill = solid(C.headerBg);
      cell.border = headerBorder;
    }
    const cell = cl(tableHeaderRow, col1);
    cell.value = {
      richText: [
        { text: zh, font: { ...FONT, size: SZ.th, bold: true, color: { argb: C.dark } } },
        { text: '\n' + en, font: { ...FONT, size: SZ.thEn, color: { argb: C.mid } } },
      ],
    };
    cell.alignment = {
      vertical: 'middle',
      horizontal: align,
      wrapText: true,
      indent: align === 'left' ? 1 : 0,
    };
  };

  writeHeader(1, 2, '產品資訊', 'Product Information', 'left');
  // 需求 R121：PM 2026-08-19 拍 Q1=A「全部統一為『保存期限』」——
  // 本表頭原為「保質期」（與 quotePdfExport.js 表頭同源，全 repo 僅這兩處），改為「保存期限」。
  // Q3「只改報價單」→ 型錄 catalogPdfExport 不動；Q4=B → productExportWriter 欄名不動。
  // Q2（資料列是否加「保存期限：」前綴）PM 回「不太理解問題」→ 維持現況不加前綴（見下方 C 欄）。
  writeHeader(3, 3, '規格 / MOQ / 保存期限', 'Spec / MOQ / Shelf Life');
  writeHeader(4, 4, '單價', 'Unit Price / CTN');
  writeHeader(5, 5, '材積、條碼', 'Cuft / CBM / Barcode');
  writeHeader(6, 6, '備註', 'Remark');

  // 品項列
  // 需求 R135 Q1=A（PM 2026-08-24「照字面：直接把 ITEM_ROW_HEIGHT 設成 72」）：
  // 70 → 72（單位 pt ≈ 96px）。描述所稱「現況 56」與程式／build 產物實際的 70 對不上，
  // PM 拍板以「目標值 72」為準，不追究 56 的來源。Q2：套用範圍只有品項列（第 12 列起），
  // 表頭第 11 列(36)、Header 第 1-4 列(30/22/20/20)、spacer 第 5/10 列(4/6)、
  // 交易條件標題(22)與內容列(≥60) 一律不動。
  // 需求 R135 追加（PM 2026-08-27 逐字：「針對 Q1 高度基準對不上的問題的確如
  // 你所言，因此決議調整高度由原本調整的 72 加高至 108」）：72 → 108（pt ≈ 144px）。
  // PM 已確認 56 的基準對不上、改以絕對值 108 為準，故本處直接改常數，不做任何換算補償。
  // Q2 的套用範圍不變——仍然只有品項列，其餘所有列高一律不動。
  // 圖片尺寸（ITEM_THUMB_PX = 70）本次未獲指示，維持不變；列高變高只是上下留白變多，
  // 垂直置中由下方 cellH = ITEM_ROW_HEIGHT * 4/3 自動重算，不需要第二處同步。
  const ITEM_ROW_HEIGHT = 108; // 容納 70x70 圖片（ITEM_THUMB_PX ≈ 52.5pt）+ 三行縮小字（10/8/8）
  let curRow = tableHeaderRow + 1;

  const dataBorder = {
    top: bs(C.border, 'hair'),
    left: bs(C.border, 'hair'),
    right: bs(C.border, 'hair'),
    bottom: bs(C.border),
  };

  items.forEach((item, idx) => {
    sheet.getRow(curRow).height = ITEM_ROW_HEIGHT;

    // A：產品縮圖（ITEM_THUMB_PX 見方，找不到圖片時顯示「產品圖」placeholder）
    const imgCell = cl(curRow, 1);
    imgCell.alignment = { vertical: 'middle', horizontal: 'center' };
    imgCell.border = dataBorder;
    const imgData = productImgMap[idx];
    if (imgData) {
      imgCell.fill = solid(C.white);
      const imgId = workbook.addImage({ buffer: imgData.buffer, extension: imgData.extension });
      // A 欄寬 14ch = 103px、列高 108pt ≈ 144px。fit-contain 至 ITEM_THUMB_PX 見方並置中，
      // 避免固定 ext 對非正方形圖片造成拉伸變形（PM 留言 2026-05-18）。
      // 需求 R134 Q3=A：與 Logo 同一個欄寬換算 bug，一併改用 colWidthToPx()（原 12*7 少 5px）。
      // 需求 R135：cellW / cellH 皆讀 COL_WIDTHS_CH[0] 與 ITEM_ROW_HEIGHT，欄寬與列高一改就自動
      // 重算置中位移，不需要第二處同步（AC1 / AC7 的單一來源）。
      const cellW = colWidthToPx(COL_WIDTHS_CH[0]);
      const cellH = ITEM_ROW_HEIGHT * 4 / 3;
      const { width: pw, height: ph } = fitContain(imgData.naturalWidth, imgData.naturalHeight, ITEM_THUMB_PX, ITEM_THUMB_PX);
      const tlCol = pxToNativeCol(0, [COL_WIDTHS_CH[0]], (cellW - pw) / 2);
      const tlRow = pxToNativeRow(curRow - 1, [ITEM_ROW_HEIGHT], (cellH - ph) / 2);
      sheet.addImage(imgId, {
        tl: { ...tlCol, ...tlRow },
        ext: { width: pw, height: ph },
        editAs: 'oneCell',
      });
    } else {
      // 需求 R91：無圖片時的「產品圖」placeholder 底色由 #F0E0B2 改為白色，
      // 與有圖片分支（上方 solid(C.white)）一致；字色維持 C.mid(#8D6E63)。
      imgCell.fill = solid(C.white);
      imgCell.value = '產品圖';
      imgCell.font = { ...FONT, size: SZ.caption, color: { argb: C.mid } };
    }

    // 需求 R26：產品資訊欄位順序 → 貨編 / 中文品名 / 英文品名（PM 2026-05-18 確認）
    // B：編號 11px(8pt) codeText + 中文品名 13px(10pt) dark 500 + 英文 11px(8pt) mid
    const nameCell = cl(curRow, 2);
    const nameZh = item.productName || '—';
    const nameEn = item.englishName || '';
    // 需求 R25：舊報價單 snapshot 未存 productCode 時，fallback 到 populate 的 item.product.productCode。
    const code = item.productCode || item.product?.productCode || '';
    const richParts = [];
    if (code) {
      richParts.push({ text: code, font: { ...FONT, size: SZ.code, color: { argb: C.codeText } } });
    }
    richParts.push({ text: (code ? '\n' : '') + nameZh, font: { ...FONT, size: SZ.prodZh, bold: true, color: { argb: C.dark } } });
    if (nameEn) {
      richParts.push({ text: '\n' + nameEn, font: { ...FONT, size: SZ.prodEn, color: { argb: C.mid } } });
    }
    nameCell.value = { richText: richParts };
    nameCell.fill = solid(C.white);
    nameCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true, indent: 1 };
    nameCell.border = dataBorder;

    // C：規格 / MOQ / 保存期限 — PDF td 12px dark → 9pt（需求 R121 用字統一，原註解寫「保質期」）
    // 需求 R121 Q2 現況：shelfLife 直接以獨立一行接在 規格／MOQ 之後，不加「保存期限：」前綴；
    // PM 回「不太理解問題」故維持現況，待確認後再決定是否加前綴（型錄 PDF 是有前綴的版本）。
    // 需求 R26：條碼從規格欄移至體積欄（與 需求 R25 暫置於規格欄的版本相異，需求 R26 為最新規格）。
    const specCell = cl(curRow, 3);
    const specParts = [];
    if (item.spec && item.pcsPerBox) specParts.push(`${item.spec} x ${item.pcsPerBox}入/箱`);
    else if (item.spec) specParts.push(item.spec);
    else specParts.push('—');
    if (item.moq) specParts.push(`MOQ: ${item.moq}`);
    // 需求 R121 R2：改走共用取值（原本只讀 item.product?.shelfLife，編輯頁匯出取不到）。
    const shelfLife = getItemShelfLife(item);
    if (shelfLife) specParts.push(shelfLife);
    specCell.value = specParts.join('\n');
    specCell.fill = solid(C.white);
    specCell.font = { ...FONT, size: SZ.spec, color: { argb: C.dark } };
    specCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    specCell.border = dataBorder;

    // D：單價 12px(9pt) dark normal + 箱價 12px(9pt) mid（PDF 內聯 12px / line-height 1.4）
    const priceCell = cl(curRow, 4);
    // 需求 R140 Q2=A（PM 2026-08-28）：匯出**讀 DB 存值、不重算**。需求 R132（b981707）之前
    // 存檔的舊單，其 unitPrice 由舊公式（成本被進位兩次）算出，四種幣別都可能高一個進位單位
    // （TWD/HKD 0.1、USD/EUR 0.01）；PM 拍板 A＝不主動回寫，要等使用者開編輯頁重存才更新。
    // 詳情頁 QuoteDetailPage 與 PDF 匯出同此規則，三處刻意一致，勿只改一處。
    const unitPrice = item.unitPrice;
    // 需求 R165 Q1=A（PM 2026-09-07 拍板，改判 需求 R140 Q1=B、回到 需求 R111 Q3=A 的方向）：
    // 箱價＝**上面那個印出來的單價** × 箱入數，使用者拿計算機一定驗算得出來
    // （PM 逐字：0.72*12 = 8.64、0.82*12 = 9.84、0.49*24 = 11.76）。
    // 基準直接傳 item.unitPrice：calcBoxPrice 內部會先套與單價欄 fmtMoney 同一個 ceilMoney，
    // 兩者逐字同源，所以印出來的單價一個字都不會變（需求 R140 Q2=A 仍成立）。
    // 與 PDF 匯出刻意同一寫法，勿只改一處（需求 R76 的教訓）。
    const boxPrice = calcBoxPrice(item.unitPrice, item.pcsPerBox, currency);
    const priceRich = [];
    if (unitPrice != null) {
      priceRich.push({
        text: `${currLabel} ${fmtMoney(unitPrice, currency)}`,
        font: { ...FONT, size: SZ.price, color: { argb: C.dark } },
      });
    } else {
      priceRich.push({ text: '—', font: { ...FONT, size: SZ.price, color: { argb: C.mid } } });
    }
    if (boxPrice != null) {
      priceRich.push({
        text: `\n${currLabel} ${fmtMoney(boxPrice, currency)}/箱`,
        font: { ...FONT, size: SZ.price, color: { argb: C.mid } },
      });
    }
    priceCell.value = { richText: priceRich };
    priceCell.fill = solid(C.white);
    priceCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    priceCell.border = dataBorder;

    // E：材積、條碼 — PDF td 13px dark → 10pt；estimate 時材積部分紅字（需求 R26 維持特殊標註）
    // 需求 R26：欄位改為材積（上）+ 國際條碼（下），條碼用中棕 SZ.spec 與 PDF 對齊。
    // 需求 R37：PM 2026-05-25 確認「條碼僅為數字字串」（非條碼圖形）。
    // 為避免 EAN-13 / UPC 等長數字被 Excel 當成 number 處理而顯示為科學記號或
    // 截掉前導 0，這裡明確轉成 String + trim，並把整格 numFmt 設為 '@'（文字）。
    // 需求 R76（PM 2026-07-21 附圖回饋）：欄位表頭已標為「材積、條碼／
    // Cuft / CBM / Barcode」，資料列再於條碼數字前重複「條碼」二字屬冗贅，移除前綴
    // 只保留純數字字串（與 quotePdfExport.js 同步，該檔已於 f9753f2 移除同一前綴）。
    // 需求 R139（PM 2026-08-25 Q1=A／Q2=A／Q3／Q4）：cuft 顯示 2 位（補零），CBM 維持 3 位；
    // 不自行進位，直接顯示 item.volumeCuft 快照（calcCuft 已四捨五入到 2 位），舊單不重算。
    // 本格是 richText 字串（numFmt '@'），沒有儲存格數值格式的問題，位數完全由 fmt() 決定。
    const volCell = cl(curRow, 5);
    const cuft = item.volumeCuft;
    const cbm = cuft != null ? (cuft * 0.0283168).toFixed(3) : '—';
    const volMethod = item.volumeMethod || item.product?.volumeMethod || 'auto';
    const volText = cuft != null ? `${fmt(cuft, 2)} / ${cbm}` : '—';
    const volColor = cuft != null && volMethod === 'estimate' ? C.red : C.dark;
    const rawBarcode = item.barcode != null && item.barcode !== ''
      ? item.barcode
      : item.product?.barcode;
    const barcodeStr = rawBarcode != null ? String(rawBarcode).trim() : '';
    const volRich = [
      { text: volText, font: { ...FONT, size: SZ.td, color: { argb: volColor } } },
    ];
    if (barcodeStr) {
      volRich.push({
        text: '\n' + barcodeStr,
        font: { ...FONT, size: SZ.spec, color: { argb: C.mid } },
      });
    }
    volCell.value = { richText: volRich };
    volCell.numFmt = '@';
    volCell.fill = solid(C.white);
    volCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    volCell.border = dataBorder;

    // F：備註 — PDF td 13px dark → 10pt
    // 需求 R96（PM 2026-07-31 拍板 Q1～Q5）：改為同時輸出「葷素備註」（上）與
    // 「業務備註」（下），以同一儲存格內的換行字元分成上下兩行（本格已 wrapText: true）。
    // 不再讀已廢棄的 item.notes（UI 已無輸入元件，新增報價單固定寫入空字串，故備註欄一直空白）。
    // 兩行同字級同色，不需 richText；行序／空行規則與 PDF 共用 quoteRemark.js，確保兩邊一致。
    // 列高固定 ITEM_ROW_HEIGHT（需求 R135 起為 72pt，2026-08-27 再調為 108pt ≈ 144px），
    // 兩行 10pt 綽綽有餘，不會撐破版面。
    const remCell = cl(curRow, 6);
    remCell.value = buildRemarkText(item);
    remCell.fill = solid(C.white);
    remCell.font = { ...FONT, size: SZ.td, color: { argb: C.dark } };
    remCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    remCell.border = dataBorder;

    curRow++;
  });

  // 交易條件 TRADE TERMS
  sheet.getRow(curRow).height = 8; // spacer
  curRow++;

  const termsTitleRow = curRow;
  sheet.getRow(termsTitleRow).height = 22;
  sheet.mergeCells(termsTitleRow, 1, termsTitleRow, 6);
  const termsTitle = cl(termsTitleRow, 1);
  termsTitle.value = '交易條件 TRADE TERMS';
  termsTitle.fill = solid(C.light);
  // PDF h4 14px mid bold uppercase letter-spacing → 10.5pt bold
  termsTitle.font = { ...FONT, size: SZ.h3, bold: true, color: { argb: C.mid } };
  termsTitle.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  termsTitle.border = {
    top: bs(C.border), left: bs(C.border),
    right: bs(C.border), bottom: bs(C.border, 'hair'),
  };

  const termsBodyRow = termsTitleRow + 1;
  const notes = quote.transactionNotes
    || '1. 本報價單有效期限為30天。\n2. 付款條件：訂單確認後預付50%，出貨前付清尾款。\n3. 交貨條件：FOB 台北 (Taiwan)，運費另計。\n4. 交貨時間：訂單確認後 7-14 個工作天。\n5. 本報價不含稅金，如需開立發票請另加 5% 營業稅。';
  const termLines = (notes.match(/\n/g) || []).length + 1;
  // PDF p 14px(10.5pt) line-height 1.6 → 10.5 × 1.6 ≈ 17pt 每行；保留些許餘裕
  sheet.getRow(termsBodyRow).height = Math.max(60, termLines * 20);
  sheet.mergeCells(termsBodyRow, 1, termsBodyRow, 6);
  const termsBody = cl(termsBodyRow, 1);
  termsBody.value = notes;
  termsBody.fill = solid(C.light);
  termsBody.font = { ...FONT, size: SZ.body, color: { argb: C.dark } };
  termsBody.alignment = { vertical: 'top', horizontal: 'left', wrapText: true, indent: 1 };
  termsBody.border = {
    top: bs(C.border, 'hair'), left: bs(C.border),
    right: bs(C.border), bottom: bs(C.border),
  };

  // 列印區域：包含完整內容
  sheet.pageSetup.printArea = `A1:F${termsBodyRow}`;

  // 下載
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${quote.quoteNumber || 'quote'}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
